import type { Child } from '@tadpoles/common'

import type { FamilyUserLocalBacking } from './persistence/family-local-backing.js'
import { replacePersistedCollection } from './persistence/replace-persisted-collection.js'
import type { ServiceLifecycle } from './service-lifecycle.js'
import { UserScopedService, type UserScopedServiceScope } from './user-scoped-service-scope.js'

export class ChildService extends UserScopedService implements ServiceLifecycle {
    readonly #localBacking: FamilyUserLocalBacking
    readonly #restoreFromBacking: boolean
    #children = $state.raw<Child[]>([])

    public constructor(
        scope: UserScopedServiceScope,
        localBacking: FamilyUserLocalBacking,
        restoreFromBacking = true
    ) {
        super(scope)
        this.#localBacking = localBacking
        this.#restoreFromBacking = restoreFromBacking
    }

    public get children(): Child[] {
        return this.#children
    }

    public async initialize(): Promise<void> {
        if (!this.#restoreFromBacking) {
            return
        }

        const store = await this.#localBacking.getChildStore()
        this.hydrate(store === undefined ? [] : await store.findAll())
    }

    public resume(): void {}

    public suspend(): void {}

    public destroy(): void {
        this.hydrate([])
    }

    public hydrate(children: Child[]): void {
        this.#children = children
    }

    public replaceAll(children: Child[]): Promise<void> {
        this.hydrate(children)
        const snapshot = this.#children

        return this.enqueueCacheWrite(async () => {
            const store = await this.#localBacking.getChildStore()

            if (store !== undefined) {
                await replacePersistedCollection(store, snapshot, (child) => child.key)
            }
        })
    }

    public get(childKey: string): Child | null {
        return this.#children.find((child) => child.key === childKey) ?? null
    }

    public put(child: Child): Promise<void> {
        this.#children = [
            ...this.#children.filter((currentChild) => currentChild.key !== child.key),
            child
        ]

        return this.enqueueCacheWrite(async () => {
            const store = await this.#localBacking.getChildStore()
            await store?.put(child)
        })
    }
}
